<script setup lang="ts">
import type { Status } from "@/api/employeeMovements";
import { ElOption, ElSelect } from "element-plus";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

export type Option = {
  label: string;
  value: Status;
};

export interface Props {
  currentStatus: string;
  options: Option[];
  size?: "small" | "default";
  disabled?: boolean;
  loading?: boolean;
  static?: boolean;
}

defineProps<Props>();

const emit = defineEmits<{
  (e: "change", status: Status): void;
}>();
</script>

<template>
  <el-select
    v-loading="loading"
    :model-value="t(`timelines.statuses.${currentStatus}`, currentStatus)"
    :disabled="disabled"
    :size="size"
    :class="[`type-${currentStatus}`, size, { static, loading }]"
    :popper-append-to-body="false"
    placement="bottom"
    @change="emit('change', $event)"
  >
    <el-option
      v-for="(option, index) in options"
      :key="`option-${index}`"
      :label="option.label"
      :value="option.value"
      :class="['option', `type-${option.value}`]"
      :disabled="currentStatus === option.value"
    />
  </el-select>
</template>

<style lang="scss" scoped>
.so-select {
  --so-select-input-color: var(--so-bg-color-page);
  --so-select-input-focus-border-color: var(--so-input-bg-color);

  font-size: 0.875rem;
  width: 110px;
}

.so-select :deep(.so-input__inner) {
  padding-left: 5px;
  text-overflow: ellipsis;
}

.so-select {
  /* Status in */
  &.type-in :deep(.so-input__wrapper) {
    --so-input-bg-color: var(--so-status-selector-in-bg-color);
  }

  &.type-in:not(.disabled):hover :deep(.so-input .so-input__wrapper) {
    --so-input-bg-color: var(--so-status-selector-in-hover-bg-color);
  }

  /* Status out */
  &.type-out :deep(.so-input__wrapper) {
    --so-input-bg-color: var(--so-status-selector-out-bg-color);
  }

  &.type-out:not(.disabled):hover :deep(.so-input .so-input__wrapper) {
    --so-input-bg-color: var(--so-status-selector-out-hover-bg-color);
  }

  /* status remote */
  &.type-remote :deep(.so-input__wrapper) {
    --so-input-bg-color: var(--so-status-selector-remote-bg-color);
  }

  &.type-remote:not(.disabled):hover :deep(.so-input .so-input__wrapper) {
    --so-input-bg-color: var(--so-status-selector-remote-hover-bg-color);
  }

  /* status pending */
  &.type-pending :deep(.so-input__wrapper) {
    --so-input-bg-color: var(--so-status-selector-pending-bg-color);
  }

  &.type-pending:not(.disabled):hover :deep(.so-input .so-input__wrapper) {
    --so-input-bg-color: var(--so-status-selector-pending-hover-bg-color);
  }

  /* status denied */
  &.type-denied :deep(.so-input__wrapper) {
    --so-input-bg-color: var(--so-status-selector-denied-bg-color);
  }

  &.type-denied:not(.disabled):hover :deep(.so-input .so-input__wrapper) {
    --so-input-bg-color: var(--so-status-selector-denied-hover-bg-color);
  }
}

.so-select,
.so-select :deep(.so-input__wrapper) {
  transition: var(--so-transition-duration);
}

.so-select:hover :deep(.so-input__wrapper),
.so-select:hover :deep(.so-input__wrapper:hover) {
  box-shadow: none;
}

.so-select.static {
  pointer-events: none;
  width: 90px;
}

.so-select.static :deep(.so-input__suffix) {
  display: none;
}

.so-select :deep(.so-input).is-disabled .so-input__wrapper {
  background: var(--so-disabled-bg-color);
}

.so-select :deep(.so-input).is-focus .so-input__wrapper {
  box-shadow: none;
}

.so-select :deep(.so-input__wrapper) {
  height: 32px;
  border: none;
  box-shadow: none;
  padding: 0;
  border-radius: 50px;
  text-align: center;
}

.so-select :deep(.so-input__inner) {
  --so-input-text-color: var(--so-bg-color-page);
  --so-disabled-text-color: var(--so-bg-color-page);

  line-height: 1.25rem;
  font-weight: var(--font-medium);
  text-align: center;
  text-transform: uppercase;
}

.so-select :deep(.so-input__suffix) {
  width: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-left: 1px solid var(--so-status-selector-divide-border-color);
}

.so-select :deep(.so-input__suffix) .so-icon {
  font-size: 0.875rem;
  margin: 0;
  color: var(--so-bg-color-page);
}

.so-select--small {
  width: 85px;

  :deep(.so-input__wrapper) {
    font-size: 0.6875rem;
    height: 22px;
  }

  :deep(.so-input__suffix) {
    width: 22px;
  }
}

.so-select--disabled {
  opacity: 0.45;

  &.type-in :deep(.so-input__wrapper) {
    --so-disabled-bg-color: var(--so-status-selector-in-bg-color);
  }

  &.type-out :deep(.so-input__wrapper) {
    --so-disabled-bg-color: var(--so-status-selector-out-bg-color);
  }

  &.type-remote :deep(.so-input__wrapper) {
    --so-disabled-bg-color: var(--so-status-selector-remote-bg-color);
  }

  &.type-pending :deep(.so-input__wrapper) {
    --so-disabled-bg-color: var(--so-status-selector-pending-bg-color);
  }

  &.type-denied :deep(.so-input__wrapper) {
    --so-disabled-bg-color: var(--so-status-selector-denied-bg-color);
  }
}

.option {
  align-items: center;
  display: flex;
  font-size: 0.875rem;
  height: 32px;
  justify-content: flex-start;
  line-height: 1.5rem;
  background-color: transparent;
  padding-left: 16px;
  padding-right: 16px;
}

.option {
  :deep(span) {
    margin-left: 7px;
  }

  &:hover {
    background-color: var(--so-fill-color-light);
  }

  &::before {
    content: "";
    width: 10px;
    height: 10px;
    display: inline-block;
    border-radius: 50%;
  }

  &.is-disabled::before {
    opacity: 0.2;
  }

  &.type-in::before {
    background: var(--so-status-selector-in-bg-color);
  }

  &.type-out::before {
    background: var(--so-status-selector-out-hover-bg-color);
  }

  &.type-remote::before {
    background: var(--so-status-selector-remote-bg-color);
  }

  &.type-approve::before {
    background: var(--so-status-selector-in-bg-color);
  }

  &.type-reject::before {
    background: var(--so-status-selector-out-hover-bg-color);
  }
}

.so-select.loading :deep(.so-input__suffix) {
  display: none;
}

.so-select.loading :deep(.so-input) .so-input__inner {
  color: transparent;
  padding-right: 20px;
}

:deep(.so-loading-mask) {
  background: none;
}

:deep(.so-loading-spinner) .path {
  stroke: #fff;
}

.so-select.loading :deep(.so-select__caret.so-input__icon.so-icon-arrow-up) {
  opacity: 0;
}

.so-select.so-select--small {
  :deep(.so-loading-spinner) {
    margin-top: -10px;
  }

  :deep(.so-loading-spinner) .circular {
    height: 20px;
    width: 20px;
  }

  :deep(.so-loading-spinner) .path {
    stroke-width: 4px;
  }

  &.loading {
    width: 22px;
  }
}

.so-select:not(.so-select--small) {
  :deep(.so-loading-spinner) {
    margin-top: -15px;
  }

  :deep(.so-loading-spinner) .circular {
    height: 30px;
    width: 30px;
  }

  :deep(.so-loading-spinner) .path {
    stroke-width: 3px;
  }

  &.loading {
    width: 32px;
  }
}
</style>
